import axios from "axios";
import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {SurveyFormat, SurveyTheme, SurveyType} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyAnswer, SurveyMediaValue} from "crqlar-react-common/dist/models/survey/surveySubmission.model";

const BASE = '/v3/public/survey';

const publicSurveyApi = axios.create({baseURL: process.env.REACT_APP_API_ENDPOINT});

export type PublicSurveyPayload = {
    surveyId: string
    version: number
    name?: string
    organizationName?: string
    type?: SurveyType
    format?: SurveyFormat
    languages?: string[]
    theme?: SurveyTheme
    questions?: SurveyQuestion[]

    privacyPolicyUrl?: LocalizedText
    termsUrl?: LocalizedText
    imprintUrl?: LocalizedText
    contactUrl?: LocalizedText

    guestLanguage?: string
    /** Categories the invitation snapshot matched; absent on anonymous payloads, which know no guest. */
    guestCategoryIds?: string[] | null
    alreadySubmitted?: boolean
};

export type PublicSurveyIdentity = {
    token?: string
    surveyId?: string
    anonymousId?: string
};

const ANONYMOUS_ID_KEY = 'crqlar.survey.anonymousId.';

const mintAnonymousId = () => (
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `survey-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

export const anonymousIdForSurvey = (surveyId: string): string => {
    const key = `${ANONYMOUS_ID_KEY}${surveyId}`;

    try {
        const stored = window.localStorage.getItem(key);
        if (stored) return stored;

        const minted = mintAnonymousId();
        window.localStorage.setItem(key, minted);
        return minted;
    } catch {
        return mintAnonymousId();
    }
};

const LANGUAGE_KEY = 'crqlar.survey.language.';
const INVITATION_LANGUAGE_KEY = 'crqlar.survey.invitationLanguage.';

// Per invitation on a personal link, so a shared device opens each guest's link in their own language; per survey on
// an open link. It also greets a return visit after the short "already answered" payload, which names no languages.
const languageKey = ({token, surveyId}: PublicSurveyIdentity): string | undefined => (
    token ? `${INVITATION_LANGUAGE_KEY}${token}` : surveyId ? `${LANGUAGE_KEY}${surveyId}` : undefined
);

/** The language last chosen on this device for this link. */
export const rememberedLanguage = (link: PublicSurveyIdentity): string | undefined => {
    const key = languageKey(link);
    if (!key) return undefined;

    try {
        return window.localStorage.getItem(key) || undefined;
    } catch {
        return undefined;
    }
};

export const rememberLanguage = (link: PublicSurveyIdentity, language: string) => {
    const key = languageKey(link);
    if (!key || !language) return;

    try {
        window.localStorage.setItem(key, language);
    } catch {
    }
};

type PublicSurveySessionRequest = PublicSurveyIdentity & {
    sessionId?: string
    version?: number
    furthestQuestionId?: string
};

type PublicSurveySubmitRequest = PublicSurveyIdentity & {
    sessionId?: string
    version?: number
    language?: string
    answers?: SurveyAnswer[]
};

export type PublicSurveyValidationError = {
    questionId?: string
    code: string
    message: string
};

export const PublicSurveyErrorCode = {
    REQUIRED: 'REQUIRED',
    INVALID_LANGUAGE: 'INVALID_LANGUAGE',
    INVALID_OPTION: 'INVALID_OPTION',
    CUSTOM_TEXT_NOT_ALLOWED: 'CUSTOM_TEXT_NOT_ALLOWED',
    OUT_OF_RANGE: 'OUT_OF_RANGE',
    CHECKLIST_INCOMPLETE: 'CHECKLIST_INCOMPLETE',
    CONSENT_REQUIRED: 'CONSENT_REQUIRED',
    SIGNATURE_REQUIRED: 'SIGNATURE_REQUIRED',
    INVALID_EMAIL: 'INVALID_EMAIL',
} as const;

type PublicSurveyFailureKind =
    | 'NOT_FOUND'
    | 'GONE'
    | 'ALREADY_SUBMITTED'
    | 'VALIDATION'
    | 'UNKNOWN';

export type PublicSurveyFailure = {
    kind: PublicSurveyFailureKind
    status?: number
    errors?: PublicSurveyValidationError[]
};

export const isPublicSurveyFailure = (error: unknown): error is PublicSurveyFailure => (
    !!error && typeof error === 'object' && typeof (error as PublicSurveyFailure).kind === 'string'
);

const toFailure = (error: any): PublicSurveyFailure => {
    const status: number | undefined = error?.response?.status;
    const errors: PublicSurveyValidationError[] | undefined = error?.response?.data?.errors;

    if (status === 404) return {kind: 'NOT_FOUND', status};
    if (status === 410) return {kind: 'GONE', status};
    if (status === 409) return {kind: 'ALREADY_SUBMITTED', status};
    if (status === 400 && errors?.length) return {kind: 'VALIDATION', status, errors};

    return {kind: 'UNKNOWN', status};
};

const attempt = async <T>(call: () => Promise<{data: T}>): Promise<T> => {
    try {
        return (await call()).data;
    } catch (error) {
        throw toFailure(error);
    }
};

export const getAnonymousSurvey = (surveyId: string) => attempt<PublicSurveyPayload>(
    () => publicSurveyApi.get(`${BASE}/anonymous/${encodeURIComponent(surveyId)}`)
);

export const getInvitationSurvey = (token: string) => attempt<PublicSurveyPayload>(
    () => publicSurveyApi.get(`${BASE}/invitation/${encodeURIComponent(token)}`)
);

export const upsertPublicSurveySession = (request: PublicSurveySessionRequest) => (
    attempt<{sessionId: string}>(() => publicSurveyApi.post(`${BASE}/session`, request))
);

export const submitPublicSurvey = (request: PublicSurveySubmitRequest) => (
    attempt<{submissionId: string}>(() => publicSurveyApi.post(`${BASE}/submit`, request))
);

export const uploadPublicSurveyMedia = (
    identity: PublicSurveyIdentity,
    questionId: string,
    file: File,
    onProgress?: (percent: number) => void
) => {
    const form = new FormData();
    if (identity.token) form.append('token', identity.token);
    if (identity.surveyId) form.append('surveyId', identity.surveyId);
    if (identity.anonymousId) form.append('anonymousId', identity.anonymousId);
    form.append('questionId', questionId);
    form.append('file', file);

    return attempt<SurveyMediaValue>(() => publicSurveyApi.post(`${BASE}/upload/media`, form, {
        onUploadProgress: event => {
            if (!onProgress) return;
            const ratio = event.total ? event.loaded / event.total : event.progress;
            if (ratio !== undefined) onProgress(Math.round(Math.min(1, ratio) * 100));
        },
    }));
};

export const uploadPublicSurveySignature = (
    identity: PublicSurveyIdentity,
    questionId: string,
    imageDataUrl: string
) => attempt<SurveyMediaValue>(
    () => publicSurveyApi.post(`${BASE}/upload/signature`, {...identity, questionId, imageDataUrl})
);
